import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  SimpleChanges,
  WritableSignal,
  signal,
} from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subscription, take } from 'rxjs';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  Classification,
  ClassificationAnswer,
  ClassificationTypeAnswer,
  ClassificationTypes,
} from '../../../core/models/question/question-classification.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-classification',
  templateUrl: './question-classification.component.html',
  styleUrls: ['./question-classification.component.scss'],
  standalone: false,
})
export class QuestionClassificationComponent implements OnInit, OnChanges {
  value: FormControl<string>;

  selectedPOClassification: Classification[];
  selectedFOClassification: Classification[];
  ClassificationTypes = ClassificationTypes;
  parsedClassificationAnswer: WritableSignal<ClassificationAnswer> =
    signal(undefined);
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  @Input() questionsInRow: number;
  @Input() question: Question;
  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;

  _questionForm: FormGroup<ControlsOf<FormQuestionStandard>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionStandard>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  get questionForm() {
    return this._questionForm;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private formsQuery: RendererFormsQuery,
  ) {}

  ngOnInit(): void {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    this.setParsedAnswer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setParsedAnswer());
    }
  }

  private setParsedAnswer() {
    this.parsedClassificationAnswer.set(
      this.value.value
        ? JSON.parse(this.value.value)
        : {
            BO: { typeId: '', choosenItems: [] },
            FO: { typeId: '', choosenItems: [] },
            PO: { typeId: '', choosenItems: [] },
          },
    );
  }

  updateAnswer(answer?: ClassificationTypeAnswer) {
    if (!answer) {
      return;
    }

    switch (answer.typeId) {
      case ClassificationTypes.BO: {
        const parsedAnswer = this.parsedClassificationAnswer();
        parsedAnswer.BO = answer;
        this.parsedClassificationAnswer.set(parsedAnswer);
        break;
      }
      case ClassificationTypes.FO: {
        const parsedAnswer = this.parsedClassificationAnswer();
        parsedAnswer.FO = answer;
        this.parsedClassificationAnswer.set(parsedAnswer);
        break;
      }
      case ClassificationTypes.PO: {
        const parsedAnswer = this.parsedClassificationAnswer();
        parsedAnswer.PO = answer;
        this.parsedClassificationAnswer.set(parsedAnswer);
        break;
      }
      default: {
        return;
      }
    }

    this.value.markAsTouched();
    this.value.markAsDirty();
    this.value.patchValue(JSON.stringify(this.parsedClassificationAnswer()));
  }
}
